import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useP } from '../i18n/useP';
import { planningLevel } from '../nav/planningLevel';
import CardShell from '../components/CardShell';
import SegmentedToggle from '../components/SegmentedToggle';
import { Cell } from '../components/InlineCells';
import { effectiveCreditLine } from '../api/creditLines';
import { getBsView } from '../api/financials_bs';
import { getForecastAssumptions, putForecastAssumption } from '../api/forecastAssumptions';
import { getPayroll } from '../api/payroll';
import { getVatPlan, VAT_PERIOD_LBL } from '../api/vatPlan';
import CashPlanChart from './financials/CashPlanChart';
import { usePersistentValueLocal } from '../hooks/usePersistentState';
import './ForecastCashPlan.css';

/* PLAN DE LIQUIDITÉ — l'écran LIT le plan calculé par le serveur (2026-09-27).

   Le calcul vivait dans le navigateur (`cashPlanDirect.js`), à côté d'un
   second moteur en Python que le bilan consommait : deux résultats pour une
   même trésorerie, donc un écart de réconciliation garanti. Le bilan publie
   désormais `cash_plan` (`cash_plan_service`), et cet écran l'affiche, mois
   par mois, sur l'HORIZON choisi : les mois ouverts de l'exercice, prolongés
   par l'exercice suivant quand son prévisionnel existe.

   Le POINT DE DÉPART se tape dans l'entête de la carte (simuler à partir d'une
   autre trésorerie) ; « Tirer de Finance » efface la saisie et le plan repart
   du dernier solde réel. Le bilan, lui, repart toujours du réel : un point de
   départ tapé ne le déplace pas. */

const MONTHS_ABBR = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'];

const fmt = (v, unit) => {
  if (v == null || !Number.isFinite(Number(v))) return '—';
  const n = Math.round(Number(v) / (unit === 'chf' ? 1 : 1000));
  return n === 0 ? '–' : n.toLocaleString('de-CH');
};

export default function ForecastCashPlanTab({ activeTenant, year, unit = 'kchf', pane: paneProp, onPane, closeAction = null, reloadKey = 0 }) {
  const { t } = useTranslation();
  const p = useP();
  const tid = activeTenant?.id;
  const N = year;
  const [bsView, setBsView] = useState(null);
  const [bsPrev, setBsPrev] = useState(null);
  const [bsNext, setBsNext] = useState(null);
  /* Vue courante : pilotée par la page hôte quand elle porte la barre standard
     (Plan de liquidité), sinon état local (usage en hub). */
  const [paneLocal, setPaneLocal] = useState('plan');
  const pane = paneProp || paneLocal;   // 'chart' | 'plan' | 'vat' | 'social'
  const setPane = onPane || setPaneLocal;
  const [showVat, setShowVat] = useState(false);    // mémo « dont TVA » sur les flux TTC
  const [foldIn, setFoldIn] = useState(true);       // groupe « Autres encaissements »
  const [foldOut, setFoldOut] = useState(true);     // groupe « Autres décaissements »
  const [foldOpex, setFoldOpex] = useState(true);   // détail des charges d'exploitation (2026-10-03)
  const [foldFin, setFoldFin] = useState(false);    // groupe « Autres flux de financement »
  /* HORIZON, en tête de page : l'exercice (ses mois ouverts), douze mois, ou
     tout ce qui existe (dont le libellé porte le nombre réel de mois). Il vaut
     pour la table comme pour le graphique : un seul choix, une seule lecture. */
  const [winRaw, setWin] = usePersistentValueLocal('fcp_chart_win2', 'all');
  /* Le choix « l'exercice » SUIT l'année lue (2026-09-28) : mémorisé sous son
     millésime, il ne valait plus rien une fois l'année changée, et la table
     retombait sur tout l'horizon, 24 mois. */
  const win = /^\d{4}$/.test(String(winRaw)) ? 'year' : winRaw;
  const [winSlot, setWinSlot] = useState(null);
  const kpisRef = useRef(null);
  const [kpisHost, setKpisHost] = useState(null);
  useLayoutEffect(() => { setKpisHost(pane === 'chart' ? kpisRef.current : null); }, [pane]);
  useLayoutEffect(() => {
    setWinSlot((pane === 'chart' || pane === 'plan') && onPane ? document.getElementById('lk-views-slot') : null);
  }, [pane, onPane]);
  const [creditLimit, setCreditLimit] = useState(0);
  const [soc, setSoc] = useState({ assum: {} });
  const [socialPlan, setSocialPlan] = useState(null);
  const [vp, setVp] = useState(null);

  const load = useCallback(() => {
    if (!tid) { setBsView(null); return; }
    getBsView(tid, N, { flatForecast: true }).then((r) => setBsView(r.error ? null : r.data)).catch(() => setBsView(null));
    getBsView(tid, N - 1).then((r) => setBsPrev(r.error ? null : r.data)).catch(() => setBsPrev(null));
    // Exercice suivant : PROLONGE le plan quand son prévisionnel existe.
    getBsView(tid, N + 1, { flatForecast: true }).then((r) => setBsNext(r.error ? null : r.data)).catch(() => setBsNext(null));
    effectiveCreditLine(tid, N).then((r) => { const vals = Object.values(r?.data || {}).map(Number).filter(Number.isFinite); setCreditLimit(vals.length ? Math.max(...vals) : 0); }).catch(() => setCreditLimit(0));
    // Le détail des charges sociales vient des Salaires prévisionnels (2026-09-30) :
    // le même calcul que la table qu'on y saisit, une seule source.
    getPayroll(tid, N).then((r) => setSocialPlan(r.error ? null : (r.data?.social || null))).catch(() => setSocialPlan(null));
    getVatPlan(tid, N).then((r) => setVp(r.error ? {} : r.data)).catch(() => setVp({}));
    getForecastAssumptions(tid, N)
      .then((a) => setSoc({ assum: (a && a.data) || {} }))
      .catch(() => setSoc({ assum: {} }));
  }, [tid, N]);
  // `reloadKey` : une hypothèse a changé dans le volet, le plan se relit.
  useEffect(() => { load(); }, [load, reloadKey]);

  /* Réglages du décompte TVA — hypothèse `vat_plan` du tenant × année :
     périodicité (trimestriel par défaut en Suisse), délai de paiement en mois
     après la fin de période (60 j = 2 mois), et la ligne de bilan qui porte la
     TVA due. AUCUN montant saisi : le solde se reconstitue des flux. */
  const vatCfg = useMemo(() => {
    const raw = soc.assum?.vat_plan;
    const o = (raw && typeof raw === 'object') ? raw : {};
    const PERIOD = { monthly: 'month', quarterly: 'quarter', semiannual: 'semester', annual: 'year' };
    return {
      period: PERIOD[activeTenant?.vat_periodicity || 'quarterly'] || 'quarter',
      lag: Number.isFinite(Number(activeTenant?.vat_payment_lag)) ? Number(activeTenant.vat_payment_lag) : 2,
      payments: (o.payments && typeof o.payments === 'object') ? o.payments : {},
      refunds: (o.refunds && typeof o.refunds === 'object') ? o.refunds : {},
    };
  }, [soc.assum, activeTenant]);
  const setVatCfg = (patch) => {
    const next = { ...vatCfg, ...patch };
    setSoc((s0) => ({ ...s0, assum: { ...(s0.assum || {}), vat_plan: next } }));
    if (tid) {
      putForecastAssumption(tid, N, 'vat_plan', { payments: next.payments, refunds: next.refunds })
        .then(() => load()).catch(() => {});
    }
  };

  /* Réglages de la vue « Charges sociales » : uniquement les postes de bilan
     suivis. Les taux, parts et ÉCHÉANCES viennent de People. */
  const socCfg = useMemo(() => {
    const o = (soc.assum?.social_cash_plan && typeof soc.assum.social_cash_plan === 'object') ? soc.assum.social_cash_plan : {};
    return { lines: Array.isArray(o.lines) ? o.lines : [], modes: (o.modes && typeof o.modes === 'object') ? o.modes : {} };
  }, [soc.assum]);
  const setSocCfg = (patch) => {
    const next = { ...socCfg, ...patch };
    setSoc((s0) => ({ ...s0, assum: { ...(s0.assum || {}), social_cash_plan: next } }));
    if (tid) putForecastAssumption(tid, N, 'social_cash_plan', next).catch(() => {});
  };

  const cp = bsView?.cash_plan || null;
  /* L'exercice suivant n'est retenu que s'il porte des flux : un prévisionnel
     N+1 non construit donnerait des colonnes à zéro, ce qui vaudrait pire que
     rien (2026-08-17). */
  const cpNext = useMemo(() => {
    const c = bsNext?.cash_plan;
    if (!c || !(c.months || []).some((m) => Math.abs(Number(c.flux?.[m]) || 0) > 0.5)) return null;
    return c;
  }, [bsNext]);

  if (!tid) return <p className="fcp-loading">{p("Sélectionne d'abord une entreprise.")}</p>;
  if (!cp) return <p className="fcp-loading">{t('common:status.loading')}</p>;

  const closed = Number(bsView?.closed_count) || 0;
  const fcMonths = cp.months || [];
  const div = unit === 'chf' ? 1 : 1000;

  /* LES COLONNES : les mois ouverts de N, puis ceux de N+1, coupés à l'horizon. */
  const allCols = [
    ...fcMonths.map((m) => ({ key: `${N}-${m}`, y: N, m, label: MONTHS_ABBR[m - 1], plan: cp })),
    ...(cpNext ? (cpNext.months || []).map((m) => ({ key: `${N + 1}-${m}`, y: N + 1, m, label: `${MONTHS_ABBR[m - 1]} ${String(N + 1).slice(2)}`, plan: cpNext })) : []),
  ];
  const cols = win === 'year' ? allCols.filter((c) => c.y === N)
    : win === '12' ? allCols.slice(0, 12)
      : allCols;
  const yearBands = cols.reduce((acc, c) => {
    const last = acc[acc.length - 1];
    if (last && last.y === c.y) last.n += 1; else acc.push({ y: c.y, n: 1 });
    return acc;
  }, []);
  const at = (c, key) => Number(c.plan.rows?.[key]?.[c.m]) || 0;
  const series = (key) => Object.fromEntries(cols.map((c) => [c.key, at(c, key)]));
  const add = (...objs) => Object.fromEntries(cols.map((c) => [c.key, objs.reduce((s2, o) => s2 + (Number(o[c.key]) || 0), 0)]));
  const sumRow = (obj) => cols.reduce((s2, c) => s2 + (Number(obj[c.key]) || 0), 0);

  const clientsOpen = series('clients_open');
  const clientsNew = series('clients_new');
  const advances = series('advances');
  const movesIn = series('moves_in');
  const salaries = series('salaries');
  const social = series('social');
  const persOther = series('personnel_other');
  const suppliersOpen = series('suppliers_open');
  const suppliersNew = series('suppliers_new');
  const opex = series('opex');
  const financial = series('financial');
  const tax = series('tax');
  const vat = series('vat');
  const movesOut = series('moves_out');
  const capex = series('capex');
  const assets = series('assets');
  const financing = series('financing');
  const repayment = series('repayment');
  const finOther = series('fin_other');
  const memo = (key) => Object.fromEntries(cols.map((c) => [c.key, Number(c.plan.vat_memo?.[key]?.[c.m]) || 0]));
  const hasAdvIn = cols.some((c) => Math.abs(advances[c.key]) > 0.5);

  const totalEnc = add(clientsOpen, clientsNew, advances, movesIn);
  const totalDec = add(salaries, social, persOther, suppliersOpen, suppliersNew, opex, financial, tax, vat, movesOut);
  const opFlux = add(totalEnc, totalDec);
  const invSub = add(capex, assets);
  const finSub = add(financing, repayment, finOther);
  const totalVar = add(opFlux, invSub, finSub);
  /* Le solde court sur les colonnes affichées depuis le point de départ : sur
     N il vaut ce que le serveur donne ; sur N+1 il enchaîne sur décembre. */
  const opening = Number(cp.opening) || 0;
  const solde = {}; const openingSeries = {};
  let cum = opening;
  cols.forEach((c) => { openingSeries[c.key] = cum; cum += totalVar[c.key]; solde[c.key] = cum; });

  /* Les lignes saisies, par côté, sur les colonnes affichées. */
  const detail = (side) => {
    const out = new Map();
    cols.forEach((c) => (c.plan.moves || []).filter((mv) => mv.side === side).forEach((mv) => {
      const k = mv.id || mv.label;
      const row = out.get(k) || { label: mv.label, vals: {} };
      row.vals[c.key] = Number(mv.months?.[String(c.m)] ?? mv.months?.[c.m]) || 0;
      out.set(k, row);
    }));
    return [...out.values()];
  };
  const detailIn = detail('in');
  const detailOut = detail('out');
  /* LE DÉTAIL DES CHARGES D'EXPLOITATION (demande d'Olivier, 2026-10-03) : un
     compte par ligne, TTC, tel que le serveur l'a payé ; la somme refait la
     ligne. Montants déjà signés (sortie négative). */
  const opexLines = (() => {
    const out = new Map();
    cols.forEach((c) => (c.plan.opex_detail || []).forEach((d) => {
      const row = out.get(d.id) || { label: d.label, vals: {} };
      row.vals[c.key] = Number(d.months?.[String(c.m)] ?? d.months?.[c.m]) || 0;
      out.set(d.id, row);
    }));
    return [...out.values()];
  })();

  const numCells = (obj, neg = false) => cols.map((c) => {
    const v = neg ? -Math.abs(obj[c.key]) : obj[c.key];
    return <td key={c.key} className={`fcp-num${Number(v) < 0 ? ' is-neg' : ''}`}>{fmt(v, unit)}</td>;
  });
  /* LE PLAN SIMPLE OU PRO (décision Olivier, 2026-09-28) : Pro, tout se lit,
     sans colonne de source ni de solde initial ; Simple, les ventes, achats,
     charges, investissements et frais de personnel se lisent de Ventes & Ops,
     le reste se tape dans la table (fond jaune). La source des salaires et
     des charges sociales se lit en gris après leur libellé. */
  const simple = cp.plan_mode?.value === 'simple';
  /* LES MÊMES LIGNES QUE LE PRO (demande d'Olivier, 2026-09-29) : en Simple,
     celles que Ventes & Ops ne nourrit pas se tapent, mois par mois. */
  const TYPED = ['clients_open', 'suppliers_open', 'moves_in', 'moves_out', 'personnel_other',
    'financial', 'tax', 'vat', 'financing', 'repayment'];
  const saveCash = (chf) => {
    setSoc((s0) => ({ ...s0, assum: { ...(s0.assum || {}), cash_start: chf == null ? null : { amount: chf } } }));
    putForecastAssumption(tid, N, 'cash_start', chf == null ? null : { amount: chf }).then(() => load()).catch(() => {});
  };
  const saveManual = (k, m, chf) => {
    const cur = (soc.assum?.cash_manual_rows && typeof soc.assum.cash_manual_rows === 'object') ? soc.assum.cash_manual_rows : {};
    const row = { ...(cur[k] || {}) };
    if (chf == null) delete row[String(m)]; else row[String(m)] = Math.abs(chf);
    const next = { ...cur, [k]: row };
    setSoc((s0) => ({ ...s0, assum: { ...(s0.assum || {}), cash_manual_rows: next } }));
    putForecastAssumption(tid, N, 'cash_manual_rows', next).then(() => load()).catch(() => {});
  };
  const isManual = (c, k) => !!k && TYPED.includes(k) && c.y === N && c.plan.plan_mode?.value === 'simple';
  const rowCells = (obj, k) => cols.map((c) => {
    if (!isManual(c, k)) {
      const v = obj[c.key];
      return <td key={c.key} className={`fcp-num${Number(v) < 0 ? ' is-neg' : ''}`}>{fmt(v, unit)}</td>;
    }
    const v = obj[c.key];
    // On tape le montant, il se lit au signe de sa ligne (une sortie en négatif).
    // Une entrée (créances, autres encaissements, emprunts) se lit positive.
    const sg = ['clients_open', 'moves_in', 'financing'].includes(k) ? 1 : -1;
    return (
      <td key={c.key} className="fcp-num fcp-editcell fcp-manual">
        <Cell value={v ? Math.round(Math.abs(Number(v)) / div) : null} type="number" canWrite placeholder="–"
              format={(x) => (x == null ? '' : fmt(sg * x * div, unit))}
              onSave={(x) => saveManual(k, c.m, x == null ? null : x * div)} />
      </td>
    );
  });
  /* LE SOLDE INITIAL, DISCRET (demande d'Olivier, 2026-09-29) : en Pro, il
     s'écrit en gris après le libellé du poste, « s.i. 512 », sans colonne ni
     couleur. La source ne s'écrit pas (demande d'Olivier, même jour). */
  const siOf = (k) => {
    const v = cp.opening_items?.rows?.[k];
    return v == null || Math.abs(Number(v)) < 0.5 ? null : `${p('s.i.')} ${fmt(Math.abs(Number(v)), unit)}`;
  };
  const withSi = (label, k) => {
    const si = simple ? null : siOf(k);
    return <>{label}{si && <span className="fcp-tag">{si}</span>}</>;
  };
  // La date du point de départ, à la même place.
  const lastDay = closed >= 1 ? new Date(N, closed, 0) : new Date(N - 1, 12, 0);
  const startTag = `${p('au')} ${String(lastDay.getDate()).padStart(2, '0')}.${String(lastDay.getMonth() + 1).padStart(2, '0')}`;
  const dataRow = (label, obj, cls = '', key = null) => (
    <tr className={cls}><td className="fcp-lbl">{label}</td>{rowCells(obj, key)}<td className="fcp-num fcp-total">{fmt(sumRow(obj), unit)}</td></tr>
  );
  // Le solde de départ du plan Simple : il se tape dans le premier mois.
  const startRow = () => (
    <tr className="fcp-open">
      <td className="fcp-lbl">{p('Solde de départ')}</td>
      {cols.map((c, i) => {
        if (i > 0) return <td key={c.key} className="fcp-num" />;
        const canType = !!cp.opening_items?.editable?.cash;
        const val = cp.opening_items?.rows?.cash;
        return canType ? (
          <td key={c.key} className="fcp-num fcp-editcell fcp-manual">
            <Cell value={val == null ? null : Math.round(Number(val) / div)} type="number" canWrite placeholder="–"
                  format={(x) => (x == null ? '' : fmt(x * div, unit))}
                  onSave={(x) => saveCash(x == null ? null : x * div)} />
          </td>
        ) : <td key={c.key} className="fcp-num">{fmt(openingSeries[c.key], unit)}</td>;
      })}
      <td className="fcp-num fcp-total" />
    </tr>
  );
  const balRow = (label, obj, cls) => (
    <tr className={cls}>
      <td className="fcp-lbl">{label}</td>
      {cols.map((c) => <td key={c.key} className={`fcp-num${Number(obj[c.key]) < 0 ? ' is-neg' : ''}`}>{fmt(obj[c.key], unit)}</td>)}
      <td className="fcp-num fcp-total" />
    </tr>
  );
  const groupRows = (label, total, lines, folded, setFolded, side) => {
    if (!lines.length) return null;
    return (
      <>
        <tr className="fcp-grp" onClick={() => setFolded((v) => !v)}>
          {/* Le libellé s'aligne sur les autres ; le chevron le suit, au survol
              seulement (demande d'Olivier, 2026-09-28). */}
          <td className="fcp-lbl">
            {label}
            <span className={`fcp-chev fcp-chev--after${folded ? '' : ' is-open'}`} aria-hidden>›</span>
          </td>
          {numCells(total)}
          <td className="fcp-num fcp-total">{fmt(sumRow(total), unit)}</td>
        </tr>
        {!folded && lines.map((ln, i) => {
          const vals = Object.fromEntries(cols.map((c) => [c.key, side === 'out' ? -(ln.vals[c.key] || 0) : (ln.vals[c.key] || 0)]));
          return (
            <tr key={i} className="fcp-memo fcp-in">
              <td className="fcp-lbl">{ln.label || p('mouvement')}</td>
              {numCells(vals)}
              <td className="fcp-num fcp-total">{fmt(sumRow(vals), unit)}</td>
            </tr>
          );
        })}
      </>
    );
  };

  /* AUTRES FLUX DE FINANCEMENT (décision Olivier, 2026-09-30) : des lignes
     nommées qu'on ajoute pour SIMULER un emprunt, un remboursement, un cash
     pooling. On tape un montant SIGNÉ (entrée +, sortie −) sur les mois ouverts
     de l'exercice ; la contrepartie est la ligne « Financement » des dettes à
     long terme du bilan, calculée au serveur. */
  const finLines = Array.isArray(soc.assum?.cash_fin_other) ? soc.assum.cash_fin_other : [];
  const saveFin = (next) => {
    setSoc((s0) => ({ ...s0, assum: { ...(s0.assum || {}), cash_fin_other: next } }));
    putForecastAssumption(tid, N, 'cash_fin_other', next).then(() => load()).catch(() => {});
  };
  const addFin = () => saveFin([...finLines, { id: `f${Date.now()}`, label: '', months: {} }]);
  const patchFin = (id, patch) => saveFin(finLines.map((ln) => (ln.id === id ? { ...ln, ...patch } : ln)));
  const setFinMonth = (ln, m, chf) => {
    const months = { ...(ln.months || {}) };
    if (chf == null || !chf) delete months[String(m)]; else months[String(m)] = chf;
    patchFin(ln.id, { months });
  };
  const finGroup = () => (
    <>
      <tr className="fcp-grp" onClick={() => setFoldFin((v) => !v)}>
        <td className="fcp-lbl">
          {p('Autres flux de financement')}
          <span className={`fcp-chev fcp-chev--after${foldFin ? '' : ' is-open'}`} aria-hidden>›</span>
          <button type="button" className="fcp-addln"
                  onClick={(e) => { e.stopPropagation(); setFoldFin(false); addFin(); }}>＋ {p('Ligne')}</button>
        </td>
        {numCells(finOther)}
        <td className="fcp-num fcp-total">{fmt(sumRow(finOther), unit)}</td>
      </tr>
      {!foldFin && finLines.map((ln) => {
        const vals = Object.fromEntries(cols.map((c) => [c.key, c.y === N ? Number(ln.months?.[String(c.m)]) || 0 : 0]));
        return (
          <tr key={ln.id} className="fcp-finln">
            <td className="fcp-lbl">
              <Cell value={ln.label} canWrite placeholder={p('Libellé')} onSave={(v) => patchFin(ln.id, { label: v || '' })} />
              <button type="button" className="lk-x lk-del" aria-label={p('Supprimer')}
                      onClick={() => saveFin(finLines.filter((x) => x.id !== ln.id))}>×</button>
            </td>
            {cols.map((c) => (c.y !== N ? <td key={c.key} className="fcp-num" /> : (
              <td key={c.key} className="fcp-num fcp-editcell fcp-manual">
                <Cell value={vals[c.key] ? vals[c.key] / div : null} type="number" canWrite placeholder="–"
                      format={(x) => (x == null ? '' : fmt(x * div, unit))}
                      onSave={(x) => setFinMonth(ln, c.m, x == null ? null : x * div)} />
              </td>
            )))}
            <td className="fcp-num fcp-total">{fmt(sumRow(vals), unit)}</td>
          </tr>
        );
      })}
    </>
  );

  // Graphique : le MÊME que le Cockpit (LiqProj) : solde de clôture réel +
  // prévisionnel + N-1 + ligne de crédit.
  const liqSeries = (bsv) => {
    const line = (bsv?.lines || []).find((l) => l.std_key === 'liquidity');
    const r = line && (bsv?.rows || []).find((x) => x.code === line.id);
    if (!r) return null;
    return Array.from({ length: 12 }, (_, i) => { const v = Number(r.months?.[String(i + 1)]); return Number.isFinite(v) ? v : null; });
  };
  const n1M = liqSeries(bsPrev);
  /* L'HISTORIQUE DE LA COURBE (2026-10-12) : en Pro, sur l'année N, les mois clos de
     la trésorerie du bilan s'ajoutent à gauche du plan, avec l'année précédente. */
  const liqN = liqSeries(bsView);
  const history = (planningLevel(activeTenant) === 'pro' && win === 'year' && liqN)
    ? Array.from({ length: Math.min(closed, 12) }, (_, i) => ({ label: MONTHS_ABBR[i], solde: liqN[i], n1: n1M ? n1M[i] : null }))
      .filter((h) => Number.isFinite(h.solde))
    : [];
  const chartSeries = {
    history,
    months: cols.map((c) => c.key),
    labels: cols.map((c) => c.label),
    enc: totalEnc,
    dec: add(totalDec, invSub, finSub),
    solde,
    opening,
    n1: cols.map((c) => (c.y === N && n1M ? n1M[c.m - 1] : null)),
  };
  const chartYears = [...new Set(allCols.map((c) => String(c.y)))];

  /* L'HORIZON MONTE SUR LA LIGNE DES COMMANDES : la section l'accueille dans sa
     rangée (`#lk-views-slot`), au costume de la charte. Sans place d'accueil
     (usage en hub), il reste dans l'entête. */
  const winOptions = [
    ...chartYears.map((y) => ({ value: y === String(N) ? 'year' : y, label: y })),
    ...(allCols.length > 12 ? [{ value: '12', label: `12 ${p('mois')}` }] : []),
    {
      value: 'all',
      label: chartYears.length > 1
        ? `${chartYears[0]}-${chartYears[chartYears.length - 1]}`
        : `${allCols.length} ${p('mois')}`,
      title: `${allCols.length} ${p('mois')}`,
    },
  ].filter((o, i, arr) => o.value !== String(N + 1) && arr.findIndex((x) => x.value === o.value) === i);
  const winSwitch = (
    <SegmentedToggle size="lg" label={p('Horizon')} value={win} onChange={setWin} options={winOptions} />
  );

  const vatAccrued = {
    billed: vp?.billed || [], dedPurch: vp?.ded_purchases || [], dedOpex: vp?.ded_opex || [],
  };
  const vatNet = Array.from({ length: 12 }, (_, i) => Number(vp?.net?.[i]) || 0);
  const vatSchedule = { payments: vp?.computed_payments || Array(12).fill(0) };
  const vatOpening = (() => {
    let cum2 = 0;
    for (let m = 1; m <= closed; m += 1) cum2 += vatNet[m - 1] - Math.abs(vatSchedule.payments[m - 1] || 0);
    return cum2;
  })();
  const vatPayEff = (m) => Number(vp?.payments_effective?.[m - 1]) || 0;

  return (
    <>
    {pane === 'chart' && <div className="fcp-kpis-host" ref={kpisRef} />}
    <CardShell
      className="report-card"
      /* Le DÉTAIL des charges sociales porte son propre titre, sans les
         commandes du plan (graphique, part de TVA) ; son solde ouvert se règle
         dans le volet des Hypothèses (demande d'Olivier, 2026-09-27). */
      title={pane === 'social' ? p('Flux charges sociales') : t('phrases:p_446d6f8d03')}
      primaryAction={closeAction}
      viewControls={pane === 'social' ? null : (
        <div className="fcp-panes">
          {!onPane && (
            <button type="button" className={pane === 'chart' ? 'is-on' : ''}
                    onClick={() => setPane('chart')}>{p('Graphique')}</button>
          )}
          {pane === 'plan' && !simple && (
            <button type="button" className={showVat ? 'is-on' : ''} onClick={() => setShowVat((v) => !v)}
              title={p('Afficher la part de TVA incluse dans les flux TTC')}>{p('dont TVA')}</button>
          )}
        </div>
      )}
      dependentControls={((pane === 'chart' || pane === 'plan') && !winSlot) ? winSwitch : null}
    >
      {winSlot && createPortal(winSwitch, winSlot)}
      {pane === 'chart' ? (
        <CashPlanChart
          {...chartSeries} win="all" kpisHost={kpisHost}
          floor={creditLimit ? -Math.abs(creditLimit) : null}
        />
      ) : pane === 'social' ? (
        <SocialPane
          bsView={bsView} socialPlan={socialPlan}
          fcMonths={fcMonths} closed={closed} unit={unit}
          cfg={socCfg} onCfg={setSocCfg}
        />
      ) : pane === 'vat' ? (
        <VatPane
          bsView={bsView} vatAccrued={vatAccrued} opening={vatOpening}
          fcMonths={fcMonths} closed={closed} unit={unit}
          method={activeTenant?.vat_method || 'effective'}
          payEff={vatPayEff} schedule={vatSchedule}
          cfg={vatCfg} onCfg={setVatCfg} year={N}
        />
      ) : (
      <div className="fcp-wrap">
        <table className={`fcp fcp--plan${unit === 'chf' ? ' fcp--chf' : ''}`}>
          <thead>
            {/* DEUX EXERCICES À L'ÉCRAN, UN ENTÊTE PAR ANNÉE (demande d'Olivier,
                2026-10-03) : le bandeau dit l'année au-dessus de ses mois, au
                costume des bandeaux Réalisé | Prévisionnel. */}
            {yearBands.length > 1 && (
              <tr className="fcp-ybandrow">
                <th className="fcp-lbl" />
                {yearBands.map((b, i) => (
                  <th key={b.y} colSpan={b.n} className={`fcp-yband fcp-yband--${i === 0 ? 'a' : 'b'}`}>{b.y}</th>
                ))}
                <th className="fcp-num fcp-total" />
              </tr>
            )}
            <tr>
              <th className="fcp-lbl" />
              {cols.map((c) => <th key={c.key} className="fcp-num">{yearBands.length > 1 ? MONTHS_ABBR[c.m - 1] : c.label}</th>)}
              <th className="fcp-num fcp-total">{p('Total')}</th>
            </tr>
            <tr className="fcp-subhead-af">
              <th className="fcp-lbl" />
              {cols.map((c) => <th key={c.key} className="fcp-num fcp-af fcp-af--f">{t('phrases:p_c177c4172d')}</th>)}
              <th className="fcp-num fcp-total" />
            </tr>
          </thead>
          <tbody>
            {simple ? startRow()
              : balRow(<>{p("Solde d'ouverture")}<span className="fcp-tag">{startTag}</span></>, openingSeries, 'fcp-open')}

            {dataRow(withSi(p('Créances clients ouvertes'), 'clients_open'), clientsOpen, 'fcp-pos', 'clients_open')}
            {dataRow(p('Ventes encaissées (TTC)'), clientsNew, 'fcp-pos')}
            {!simple && showVat && dataRow(p('dont TVA'), memo('clients_new'), 'fcp-memo')}
            {hasAdvIn && dataRow(p('Acomptes clients encaissés'), advances, 'fcp-pos')}
            {simple ? dataRow(p('Autres encaissements'), movesIn, 'fcp-pos', 'moves_in')
              : groupRows(p('Autres encaissements'), movesIn, detailIn, foldIn, setFoldIn, 'in')}
            {dataRow(p('Total encaissements'), totalEnc, 'fcp-sub fcp-pos')}

            {dataRow(p('Salaires nets'), salaries, 'fcp-neg')}
            {dataRow(withSi(p('Charges sociales'), 'social'), social, 'fcp-neg')}
            {dataRow(p('Autres charges de personnel'), persOther, 'fcp-neg', 'personnel_other')}
            {dataRow(withSi(p('Dettes fournisseurs ouvertes'), 'suppliers_open'), suppliersOpen, 'fcp-neg', 'suppliers_open')}
            {dataRow(p('Achats TTC'), suppliersNew, 'fcp-neg')}
            {!simple && showVat && dataRow(p('dont TVA'), memo('suppliers_new'), 'fcp-memo')}
            {opexLines.length
              ? groupRows(p("Charges d'exploitation (TTC)"), opex, opexLines, foldOpex, setFoldOpex, 'in')
              : dataRow(p("Charges d'exploitation (TTC)"), opex, 'fcp-neg')}
            {!simple && showVat && dataRow(p('dont TVA'), memo('opex'), 'fcp-memo')}
            {dataRow(p('Intérêts & frais financiers'), financial, 'fcp-neg', 'financial')}
            {dataRow(withSi(p('Impôts payés'), 'tax'), tax, 'fcp-neg', 'tax')}
            {dataRow(withSi(p('TVA — décompte AFC'), 'vat'), vat, 'fcp-neg fcp-vat', 'vat')}
            {simple ? dataRow(p('Autres décaissements'), movesOut, 'fcp-neg', 'moves_out')
              : groupRows(p('Autres décaissements'), movesOut, detailOut, foldOut, setFoldOut, 'out')}
            {dataRow(p('Total décaissements'), totalDec, 'fcp-sub fcp-neg')}
            {dataRow(p('Flux opérationnel'), opFlux, 'fcp-level')}

            {dataRow(p("Nouveaux actifs (dépenses d'investissement)"), capex)}
            {dataRow(p('Cessions et sorties d’actifs'), assets)}
            {dataRow(p("Flux d'investissement"), invSub, 'fcp-level')}

            {dataRow(p('Emprunts et apports'), financing, '', 'financing')}
            {dataRow(p('Remboursements'), repayment, 'fcp-neg', 'repayment')}
            {finGroup()}
            {dataRow(p('Flux de financement'), finSub, 'fcp-level')}

            {dataRow(p('Variation totale de trésorerie'), totalVar, 'fcp-strong')}
            {balRow(p('Solde de clôture'), solde, 'fcp-close')}

            {/* PAS D'ÉCART AU BILAN ICI (décision Olivier, 2026-09-27) : expliquer
                ce que le bilan porte sans flux est le travail du FINANCIER, pas du
                vendeur ni du CEO. Il se lit dans Finance, sur le bilan
                prévisionnel, en « Autres variations de prévision ». */}
          </tbody>
        </table>
      </div>
      )}
    </CardShell>
    </>
  );
}

/* Barres encaissements (sage) & décaissements (terracotta) — les deux en positif,
   côte à côte par mois. */
function VatPane({ bsView, vatAccrued, opening, fcMonths, closed, unit, cfg, onCfg, year, method, payEff, schedule }) {
  const p = useP();
  const net = Array.from({ length: 12 }, (_, i) => (Number(vatAccrued?.billed?.[i]) || 0)
    - (Number(vatAccrued?.dedPurch?.[i]) || 0) - (Number(vatAccrued?.dedOpex?.[i]) || 0));
  // Courus retenus = mois PRÉVISIONNELS seulement (le passé vit dans l'ouverture).
  const accrued = net.map((v, i) => (i + 1 <= closed ? 0 : v));
  /* Solde dû recalculé sur les versements EFFECTIFS (un arrangement de paiement
     déplace le cash sans changer ce qui est dû). */
  const balance = {};
  {
    let cum = opening;
    fcMonths.forEach((m) => { cum += accrued[m - 1] - payEff(m); balance[m] = cum; });
  }
  const isOverridden = (m) => {
    const ov = (cfg.payments || {})[String(m)];
    return !(ov == null || ov === '');
  };
  // Recalcule TOUS les mois prévisionnels depuis le délai des Paramètres.
  const recalcPayments = () => {
    const next = { ...(cfg.payments || {}) };
    fcMonths.forEach((m) => { next[String(m)] = schedule.payments[m - 1] || 0; });
    onCfg({ payments: next });
  };
  const clearPayments = () => onCfg({ payments: {} });
  /* Colonnes = mois PRÉVISIONNELS uniquement (démarrage en juillet chez KeyIT) :
     le passé n'est ni rejoué ni affiché — il vit dans le solde d'ouverture. */
  const months = fcMonths;
  const at = (arr, m) => Number(arr?.[m - 1]) || 0;
  const cell = (v, cls = '') => (
    <td className={`fcp-num${Number(v) < 0 ? ' is-neg' : ''}${cls ? ' ' + cls : ''}`}>{fmt(v, unit)}</td>
  );
  const row = (label, vals, cls = '', sign = 1) => (
    <tr className={cls}>
      <td className="fcp-lbl">{label}</td>
      {months.map((m) => cell(sign * at(vals, m)))}
      <td className="fcp-num fcp-total">{fmt(months.reduce((a, m) => a + sign * at(vals, m), 0), unit)}</td>
    </tr>
  );
  return (
    <>
      <div className="fcp-cfg">
        <div className="fcp-cfg__row">
          <span>{p('Solde dû à l’ouverture')} <b>{fmt(opening, unit)}</b> {unit === 'chf' ? 'CHF' : 'kCHF'}</span>
          <span className="fcp-cfg__right">
            {p('décompte')} <b>{VAT_PERIOD_LBL[cfg.period] || cfg.period}</b>
            {' · '}{p('paiement')} <b>{cfg.lag ? `${cfg.lag * 30} ${p('jours')}` : p('à la clôture')}</b>
            {' · '}<span className="fcp-src">{p('Finance › TVA')}</span>
          </span>
        </div>
      </div>
      <div className="fcp-wrap">
        <table className={`fcp${unit === 'chf' ? ' fcp--chf' : ''}`}>
          <thead>
            <tr>
              <th className="fcp-lbl" />
              {months.map((m) => <th key={m} className="fcp-num">{MONTHS_ABBR[m - 1]}</th>)}
              <th className="fcp-num fcp-total">{p('Total')}</th>
            </tr>
            <tr className="fcp-subhead-af">
              <th className="fcp-lbl" />
              {months.map((m) => <th key={m} className="fcp-num fcp-af fcp-af--f">{p('prév.')}</th>)}
              <th className="fcp-num fcp-total" />
            </tr>
          </thead>
          <tbody>
            {/* Ouverture de CHAQUE mois = clôture du précédent (2026-08-17). */}
            <tr className="fcp-open">
              <td className="fcp-lbl">{p('Solde dû à l’ouverture')}</td>
              {months.map((m, i) => cell(i === 0 ? opening : balance[months[i - 1]]))}
              <td className="fcp-num fcp-total" />
            </tr>
            {row(p('TVA collectée sur ventes'), vatAccrued?.billed, 'fcp-pos')}
            {row(p('TVA payée sur achats'), vatAccrued?.dedPurch, 'fcp-neg', -1)}
            {row(p('TVA payée sur charges'), vatAccrued?.dedOpex, 'fcp-neg', -1)}
            {row(p('TVA nette de la période'), accrued, 'fcp-sub')}
            <tr className="fcp-neg">
              <td className="fcp-lbl">
                {p("Paiement à l'AFC")}
                <button type="button" className="fcp-recalc" onClick={recalcPayments}
                  title={p('Recalculer les versements selon la périodicité et le délai des Paramètres')}>↻</button>
                {Object.keys(cfg.payments || {}).length > 0 && (
                  <button type="button" className="fcp-recalc" onClick={clearPayments}
                    title={p('Revenir aux versements calculés')}>×</button>
                )}
              </td>
              {months.map((m) => (
                <td key={m} className={`fcp-num${isOverridden(m) ? ' is-edited' : ''}`}>
                  <PayCell chf={-payEff(m)} unit={unit}
                    onCommit={(chf) => onCfg({ payments: { ...(cfg.payments || {}), [String(m)]: Math.abs(chf) } })} />
                </td>
              ))}
              <td className="fcp-num fcp-total">{fmt(-months.reduce((a, m) => a + payEff(m), 0), unit)}</td>
            </tr>
            {row(p('Solde dû fin de mois'), balance, 'fcp-close')}
          </tbody>
        </table>
      </div>
      <div className="fcp-note">
        {method === 'tdfn' && (
          <div className="fcp-warn">
            {p('Décompte TDFN : la TVA due est un taux de branche sur le chiffre d’affaires brut, sans impôt préalable déductible. Le calcul ci-dessus suit la méthode effective — à ne pas utiliser tel quel.')}
          </div>
        )}
        {p('Décompte pur forward : le dû d’ouverture se déduit des courus et de l’échéancier — seuls les versements futurs s’ajustent (vue Finance › TVA).')}
      </div>
    </>
  );
}

/* ── Vue « Charges sociales » du plan de liquidité (2026-08-17) ──────────────
   Elle LIT le modèle de People : une ligne par charge (taux, parts) avec son
   propre échéancier (`payment_months`), et pour les assurances la prime annuelle
   posée en acomptes sur la masse déclarée. Rien n'est redéfini ici : modifier une
   échéance se fait dans People › Données › Charges sociales.
   Réglage local unique : les postes de bilan suivis (dette sociale). */
function SocialPane({ bsView, socialPlan, fcMonths, closed, unit, cfg, onCfg }) {
  const p = useP();
  // Les deux sous-totaux sont des GROUPES repliables (chevron révélé au survol).
  const [fold, setFold] = useState({ accrued: false, paid: false });
  const toggle = (k) => setFold((f) => ({ ...f, [k]: !f[k] }));
  const months = fcMonths;
  const at = (arr, m) => Number(arr?.[m - 1]) || 0;   // index 0 = janvier de N
  const MONTH_LBL = ['jan', 'fév', 'mar', 'avr', 'mai', 'jun', 'jul', 'aoû', 'sep', 'oct', 'nov', 'déc'];

  // Postes de bilan suivis (dette sociale, impôt à la source…).
  const cands = (bsView?.lines || []).filter((l) => ['current_liabilities', 'long_term_liabilities', 'current_assets'].includes(l.section));
  const isAsset = (l) => l.section === 'current_assets';
  const sign = (l) => (isAsset(l) ? -1 : 1);
  const lastReal = (id) => {
    const r = (bsView?.rows || []).find((x) => x.code === id);
    const v = closed >= 1 ? r?.months?.[String(closed)] : r?.prev_dec;
    return Number(v) || 0;
  };
  const modeOf = (id) => ((cfg.modes || {})[id] === 'manual' ? 'manual' : 'auto');
  const tracked = (cfg.lines || []).map((id) => cands.find((l) => l.id === id)).filter(Boolean);
  const opening = tracked.filter((l) => modeOf(l.id) === 'auto')
    .reduce((sum, l) => sum + sign(l) * lastReal(l.id), 0);

  // Séries de People : couru par charge, décaissement par facture.
  const charges = (socialPlan?.charges || []).filter((c) => c.hasEmpl || c.hasEmpr);
  const invoices = socialPlan?.invoices || [];
  const payMonthsOf = (libelle) => {
    const c = charges.find((x) => x.libelle === libelle);
    const pm = c?.paymentMonths;
    if (!Array.isArray(pm) || pm.length === 12) return p('tous les mois');
    if (!pm.length) return p('aucun mois');
    return pm.slice().sort((a, b) => a - b).map((i) => MONTH_LBL[i]).join(' · ');
  };
  const accruedAt = (m) => charges.reduce((s2, c) => s2 + at(c.empl, m) + at(c.empr, m), 0);
  const paidAt = (m) => invoices.reduce((s2, iv) => s2 + at(iv.inv, m), 0);
  // Solde dû : ouverture + couru − décaissé, mois après mois.
  const balance = {};
  let cum = opening;
  months.forEach((m) => { cum += accruedAt(m) - paidAt(m); balance[m] = cum; });

  const cell = (v) => <td className={`fcp-num${Number(v) < 0 ? ' is-neg' : ''}`}>{fmt(v, unit)}</td>;
  const row = (label, valAt, cls = '', sg = 1) => (
    <tr className={cls}>
      <td className="fcp-lbl">{label}</td>
      {months.map((m) => cell(sg * valAt(m)))}
      <td className="fcp-num fcp-total">{fmt(months.reduce((a, m) => a + sg * valAt(m), 0), unit)}</td>
    </tr>
  );
  return (
    <>
      {/* Les postes de bilan suivis (le solde ouvert) se règlent dans le volet
          des Hypothèses depuis le 2026-09-27 ; ce détail les lit. */}
      <div className="fcp-wrap">
        <table className={`fcp${unit === 'chf' ? ' fcp--chf' : ''}`}>
          <thead>
            <tr>
              <th className="fcp-lbl" />
              {months.map((m) => <th key={m} className="fcp-num">{MONTHS_ABBR[m - 1]}</th>)}
              <th className="fcp-num fcp-total">{p('Total')}</th>
            </tr>
            <tr className="fcp-subhead-af">
              <th className="fcp-lbl" />
              {months.map((m) => <th key={m} className="fcp-num fcp-af fcp-af--f">{p('prév.')}</th>)}
              <th className="fcp-num fcp-total" />
            </tr>
          </thead>
          <tbody>
            {/* Ouverture de CHAQUE mois = clôture du précédent (2026-08-17). */}
            <tr className="fcp-open">
              <td className="fcp-lbl">{p('Solde dû à l’ouverture')}</td>
              {months.map((m, i) => cell(i === 0 ? opening : balance[months[i - 1]]))}
              <td className="fcp-num fcp-total" />
            </tr>
            {tracked.map((l) => (
              <tr key={l.id} className="fcp-memo">
                <td className="fcp-lbl">{l.label} <span className="fcp-tag">{modeOf(l.id) === 'auto' ? p('calculé') : p('manuel')}</span></td>
                {months.map((m, i) => <td key={m} className="fcp-num">{i === 0 ? fmt(sign(l) * lastReal(l.id), unit) : ''}</td>)}
                <td className="fcp-num fcp-total" />
              </tr>
            ))}
            <tr className="fcp-grp" onClick={() => toggle('accrued')}>
              <td className="fcp-lbl">
                <span className={`fcp-chev${fold.accrued ? '' : ' is-open'}`} aria-hidden>›</span>
                {p('Cotisations courues')}
              </td>
              {months.map((m) => cell(accruedAt(m)))}
              <td className="fcp-num fcp-total">{fmt(months.reduce((a, m) => a + accruedAt(m), 0), unit)}</td>
            </tr>
            {!fold.accrued && charges.map((c) => (
              <tr key={`c-${c.libelle}`} className="fcp-memo fcp-in">
                <td className="fcp-lbl">{c.libelle} <span className="fcp-tag">{c.section === 'assurance' ? p('assurance') : p('base')}</span></td>
                {months.map((m) => cell(at(c.empl, m) + at(c.empr, m)))}
                <td className="fcp-num fcp-total">{fmt(months.reduce((a, m) => a + at(c.empl, m) + at(c.empr, m), 0), unit)}</td>
              </tr>
            ))}
            <tr className="fcp-grp" onClick={() => toggle('paid')}>
              <td className="fcp-lbl">
                <span className={`fcp-chev${fold.paid ? '' : ' is-open'}`} aria-hidden>›</span>
                {p('Décaissements estimés')}
              </td>
              {months.map((m) => cell(-paidAt(m)))}
              <td className="fcp-num fcp-total">{fmt(-months.reduce((a, m) => a + paidAt(m), 0), unit)}</td>
            </tr>
            {!fold.paid && invoices.map((iv) => (
              <tr key={`i-${iv.libelle}`} className="fcp-memo fcp-in">
                <td className="fcp-lbl">{iv.libelle} <span className="fcp-tag">{payMonthsOf(iv.libelle)}</span></td>
                {months.map((m) => cell(-at(iv.inv, m)))}
                <td className="fcp-num fcp-total">{fmt(-months.reduce((a, m) => a + at(iv.inv, m), 0), unit)}</td>
              </tr>
            ))}
            <tr className="fcp-close">
              <td className="fcp-lbl">{p('Solde dû fin de mois')}</td>
              {months.map((m) => cell(balance[m]))}
              <td className="fcp-num fcp-total" />
            </tr>
          </tbody>
        </table>
      </div>
      <div className="fcp-note">
        {tracked.length
          ? `${p('Postes suivis :')} ${tracked.map((l) => l.label).join(' · ')}`
          : p("Aucun poste de bilan suivi : l'ouverture vaut zéro, seuls les mois prévisionnels sont comptés.")}
        {' · '}
        {p('Un solde négatif = acomptes versés d’avance (créance sur la caisse), régularisée au décompte annuel.')}
      </div>
    </>
  );
}

/* Cellule de versement saisissable : bleu, sans cadre (charte). Stockage CHF,
   affichage dans l'unité courante. */
function PayCell({ chf, unit, onCommit }) {
  const [draft, setDraft] = useState(null);
  const div = unit === 'chf' ? 1 : 1000;
  const shown = draft != null ? draft : (Math.abs(Number(chf) || 0) < 0.5 ? '' : String(Math.round(Number(chf) / div)));
  const commit = () => {
    if (draft == null) return;
    const raw = String(draft).replace(/['\s’]/g, '').replace(',', '.');
    const v = parseFloat(raw);
    setDraft(null);
    onCommit(Number.isFinite(v) ? v * div : 0);
  };
  return (
    <input
      className="fcp-paycell fcp-field"
      value={shown}
      placeholder="–"
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); if (e.key === 'Escape') setDraft(null); }}
    />
  );
}
